/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import { Else, If, Then } from "react-if";
import { NumericFormat, NumberFormatValues } from "react-number-format";
import styled from "styled-components";
import { Button, FlexContainer, ModalConfirm, theme } from "@nordcloud/gnui";
import { DataItem, LoaderWrap } from "~/components";
import { isNotNil } from "~/tools";

type Props = {
  threshold: number;
  onThresholdChanged: (threshold: number) => void;
  errorTimestamp?: number;
  loading: boolean;
};

export function AnomaliesThreshold({
  threshold: originalThreshold,
  onThresholdChanged,
  errorTimestamp = undefined,
  loading,
}: Props) {
  const [threshold, setThreshold] = useState<number | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [isConfirmationOpened, setIsConfirmationOpened] = useState(false);

  const isDirty = originalThreshold !== threshold;

  useEffect(() => {
    setThreshold(originalThreshold);
  }, [originalThreshold, errorTimestamp]);

  const onValueChange = ({ floatValue }: NumberFormatValues) =>
    setThreshold(floatValue);

  const isAllowed = ({ floatValue: value }: NumberFormatValues) =>
    value === undefined ||
    (typeof value === "number" && value >= 0 && value <= 200);

  const cleanUp = () => {
    setIsConfirmationOpened(false);
    setEditing(false);
    if (threshold == null) {
      setThreshold(originalThreshold);
    }
  };

  const onConfirmationApproved = () => {
    if (threshold) {
      onThresholdChanged(threshold);
      cleanUp();
    }
  };

  const canAcceptThreshold = isDirty && isNotNil(threshold);

  return (
    <>
      <FlexContainer>
        <LoaderWrap inContent loading={loading}>
          <FlexContainer direction="column">
            <If condition={editing}>
              <Then>
                <NumericInput
                  value={threshold}
                  suffix={isNotNil(threshold) ? " %" : ""}
                  displayType="input"
                  isAllowed={isAllowed}
                  placeholder={
                    isNotNil(threshold) ? `${threshold} %` : "e.g. 10 %"
                  }
                  onValueChange={onValueChange}
                />
              </Then>
              <Else>
                <DataItem
                  grow={false}
                  weight="medium"
                  size="lg"
                  label="Anomaly Threshold"
                  value={`${originalThreshold} %`}
                  padding="0"
                />
              </Else>
            </If>
          </FlexContainer>
          <If condition={editing}>
            <Then>
              <Button
                ml={theme.spacing.spacing02}
                icon={canAcceptThreshold ? "save" : "close"}
                size="md"
                severity="low"
                data-testid={
                  canAcceptThreshold ? "save-threshold" : "close-threshold"
                }
                onClick={() =>
                  canAcceptThreshold ? setIsConfirmationOpened(true) : cleanUp()
                }
              />
            </Then>
            <Else>
              <Button
                data-testid="edit-threshold"
                icon="edit"
                size="md"
                severity="low"
                ml={theme.spacing.spacing04}
                onClick={() => setEditing(true)}
              />
            </Else>
          </If>
        </LoaderWrap>
      </FlexContainer>
      <ModalConfirm
        contentLabel="Global Anomaly Threshold"
        isOpen={isConfirmationOpened}
        confirm={() => onConfirmationApproved()}
        actionLabel="Save Threshold"
        onClose={() => setIsConfirmationOpened(false)}
      >
        The {threshold}% value will be set up as an anomaly threshold globally
        for all your applications.
      </ModalConfirm>
    </>
  );
}

const NumericInput = styled(NumericFormat)`
  border: 1px solid ${theme.color.border.input};
  width: 50%;
  box-sizing: border-box;
  background: transparent;
  background-color: ${theme.color.field.default};
  font-family: ${theme.typography.fonts.body};
  font-size: ${theme.fontSizes.md};
  border-radius: ${theme.radiusDefault};
  padding: ${theme.spacing.spacing02};
  margin: 3px 0 2px;
  color: ${theme.color.text.text01};
  align-self: flex-end;
`;
